import { memo, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { getPropertyCancellationLabelShape, getPropertyRefundableStatus, getRefundDescriptions } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import { showsPropertyCancellationTimeline } from 'lib/offer/cancellationPolicyUtils'
import PropertyCancellationPolicyView from './PropertyCancellationPolicyView'
import { getPropertyCancellationPolicyDescriptions } from './propertyCancellationPolicyDescriptions'

interface Props {
  rate: App.BasePropertyRate
  propertyTimeZone: string
  shouldClarifyRefundIsHotelOnly: boolean
}

function PropertyCancellationPolicy({
  rate,
  propertyTimeZone,
  shouldClarifyRefundIsHotelOnly,
}: Props) {
  const intl = useIntl()
  const refundDescriptions = useMemo(() => getRefundDescriptions(undefined, intl), [intl])
  const refundableStatus = getPropertyRefundableStatus(rate.headlineCancellationPolicyType)
  const refundDescription = refundDescriptions[refundableStatus]

  const policyDescriptions = useMemo(() => getPropertyCancellationPolicyDescriptions({
    fullyRefundableUntil: rate.fullyRefundableUntil,
    cancellationPolicies: rate.cancellationPolicies,
    propertyTimeZone,
    currency: rate.currency,
    shouldClarifyRefundIsHotelOnly,
    locale: intl.locale,
  }), [
    rate.fullyRefundableUntil,
    rate.cancellationPolicies,
    rate.currency,
    propertyTimeZone,
    shouldClarifyRefundIsHotelOnly,
    intl.locale,
  ])

  // TODO `formattedShortDateByTimezone` includes a hack for string timestamps suffixed with `'T00:00:00.000Z'`, which we want to avoid
  const headlineCancellationPolicyStart = useMemo(
    () => rate.headlineCancellationPolicyStart ? new Date(rate.headlineCancellationPolicyStart) : undefined,
    [rate.headlineCancellationPolicyStart],
  )

  const labelShape = useMemo(() => getPropertyCancellationLabelShape({
    refundable: refundableStatus,
    headlineCancellationPolicyStart,
    propertyTimezone: propertyTimeZone,
    shouldClarifyRefundIsHotelOnly,
  }), [refundableStatus, headlineCancellationPolicyStart, propertyTimeZone, shouldClarifyRefundIsHotelOnly])

  const showsTimeline = showsPropertyCancellationTimeline(rate)

  return <PropertyCancellationPolicyView
    refundableStatus={refundableStatus}
    refundDescription={refundDescription}
    labelShape={labelShape}
    policyDescriptions={policyDescriptions}
    timelineDeadline={showsTimeline && labelShape.kind === 'date' ? labelShape.formattedDate : undefined}
  />
}

export default memo(PropertyCancellationPolicy)
